import * as stylex from "@stylexjs/stylex";
import { colors, radii } from "@softmax/design-system/tokens.stylex";
import { useContext, type MouseEvent } from "react";
import type { Components } from "react-markdown";
import { MarkdownContext } from "./markdown-context";
import {
  resolveMarkdownLink,
  safeMarkdownHref,
  safePostImageSrc,
} from "./markdown-links";

export const markdownMediaElements = {
  a: function MarkdownLink({ children, href }) {
    const { wikiPageHref, existingPageSlugs, renderLink, variant } =
      useContext(MarkdownContext);
    const safeHref = safeMarkdownHref(href);

    if (!safeHref) return <span>{children}</span>;

    const { resolvedHref, internal, inPage, wikiRelative } =
      resolveMarkdownLink({ href: safeHref, wikiPageHref });
    const missing =
      wikiRelative &&
      existingPageSlugs !== undefined &&
      !existingPageSlugs.has(safeHref);
    const props = {
      ...stylex.props(
        styles.link,
        variant === "article" && styles.articleLink,
        missing && styles.missingLink,
      ),
      href: resolvedHref,
      children,
      "data-link-state": missing ? "missing" : undefined,
      title: missing
        ? `"${safeHref}" hasn't been written yet — follow this link to start it`
        : undefined,
      target: internal || inPage ? undefined : "_blank",
      rel: internal || inPage ? undefined : "noopener noreferrer",
      onClick: (event: MouseEvent<HTMLAnchorElement>) =>
        event.stopPropagation(),
    };
    return internal && renderLink ? (
      renderLink(props)
    ) : (
      <a {...props}>{children}</a>
    );
  },
  img: ({ src, alt }) => {
    const safeSrc = safePostImageSrc(typeof src === "string" ? src : undefined);

    if (!safeSrc) return alt ? <span>{alt}</span> : null;

    return (
      <img
        src={safeSrc}
        alt={alt ?? ""}
        referrerPolicy="no-referrer"
        loading="lazy"
        {...stylex.props(styles.image)}
      />
    );
  },
} satisfies Components;

const styles = stylex.create({
  link: {
    color: colors.foreground,
    textDecoration: "underline",
    textUnderlineOffset: 3,
    outlineOffset: 3,
    textDecorationColor: {
      default: colors.borderStrong,
      ":hover": colors.foreground,
    },
  },
  missingLink: { color: colors.attention, textDecorationStyle: "dotted" },
  image: { maxWidth: "100%", height: "auto", borderRadius: radii.small },
  articleLink: {
    textDecorationColor: {
      default: "rgba(22, 22, 22, 0.5)",
      ":hover": colors.foreground,
    },
  },
});
